var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import '../table-filter/table-filter.js';
import { createTable, getCoreRowModel, getFacetedMinMaxValues, getFacetedRowModel, getFacetedUniqueValues, getFilteredRowModel, getSortedRowModel, sortingFns, } from '@tanstack/table-core';
import { html } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { map } from 'lit/directives/map.js';
import { when } from 'lit/directives/when.js';
import { SgdfElement } from '../../internal/sgdf-element.js';
import { watch } from '../../internal/watch.js';
import styles from './table-next.styles.js';
import { compareItems, rankItem } from './table-next.utils.js';
function flexRender(func, props) {
    if (typeof func === 'function') {
        const value = func(props);
        return html `${value}`;
    }
    return html `${func}`;
}
const fuzzyFilter = (row, columnId, value, addMeta) => {
    // Rank the item
    const itemRank = rankItem(row.getValue(columnId), value);
    // Store the itemRank info
    addMeta({
        itemRank,
    });
    // Return if the item should be filtered in/out
    return itemRank.passed;
};
export const fuzzySort = (rowA, rowB, columnId) => {
    var _a, _b;
    let dir = 0;
    // Only sort by rank if the column has ranking information
    if (rowA.columnFiltersMeta[columnId]) {
        dir = compareItems((_a = rowA.columnFiltersMeta[columnId]) === null || _a === void 0 ? void 0 : _a.itemRank, (_b = rowB.columnFiltersMeta[columnId]) === null || _b === void 0 ? void 0 : _b.itemRank);
    }
    // Provide an alphanumeric fallback for when the item ranks are equal
    return dir === 0 ? sortingFns.alphanumeric(rowA, rowB, columnId) : dir;
};
/**
 * @summary Short summary of the component's intended use.
 * @status experimental
 * @since 1.0
 *
 * @dependency sgdf-example
 *
 * @event sgdf-event-name - Emitted as an example.
 *
 * @slot - The default slot.
 * @slot example - An example slot.
 *
 * @csspart base - The component's base wrapper.
 *
 * @cssproperty --example - An example CSS custom property.
 */
class SgdfTableNext extends SgdfElement {
    constructor() {
        super(...arguments);
        this.fixedLayout = false;
        this.table = null;
        this.sorting = [];
        this.columnFilters = [];
        this.onSortingChange = (updaterOrValue) => {
            this.sorting =
                typeof updaterOrValue === 'function'
                    ? updaterOrValue(this.sorting)
                    : updaterOrValue;
            this.sorting.length > 0 &&
                this.emit('sgdf-participants:sort', {
                    detail: {
                        sortField: this.sorting[0].id,
                        sortDir: this.sorting[0].desc ? 'desc' : 'asc',
                    },
                });
        };
        this.onColumnFilter = (updaterOrValue) => {
            this.columnFilters =
                typeof updaterOrValue === 'function'
                    ? updaterOrValue(this.columnFilters)
                    : updaterOrValue;
        };
        this.getColumnType = (column) => {
            var _a, _b;
            const firstValue = (_b = (_a = this.table) === null || _a === void 0 ? void 0 : _a.getPreFilteredRowModel().flatRows[0]) === null || _b === void 0 ? void 0 : _b.getValue(column.id);
            if (firstValue === undefined || firstValue === null) {
                return null;
            }
            return typeof firstValue;
        };
        this.getFilterInput = (table, column) => {
            const type = this.getColumnType(column);
            const columnFilterValue = column.getFilterValue();
            const sortedUniqueValues = type === 'number'
                ? []
                : Array.from(column.getFacetedUniqueValues().keys()).sort();
            return html `<sgdf-table-filter
            .column=${column}
            .table=${table}
            .type=${type}
            .columnFilterValue=${columnFilterValue}
            .sortedUniqueValues=${sortedUniqueValues}
            @sgdf-debounced-input=${(value) => {
                column.setFilterValue(value.detail);
            }}
        ></sgdf-table-filter>`;
        };
    }
    getDefaultSortField() {
        var _a, _b, _c;
        const first = (_a = this.columns) === null || _a === void 0 ? void 0 : _a[0];
        if (!first)
            return '';
        const anyCol = first;
        return (_c = (_b = anyCol.id) !== null && _b !== void 0 ? _b : anyCol.accessorKey) !== null && _c !== void 0 ? _c : '';
    }
    getResolvedSortField() {
        var _a;
        return (_a = this.sortField) !== null && _a !== void 0 ? _a : this.getDefaultSortField();
    }
    getResolvedSortDir() {
        return this.sortDir === 'desc' ? 'desc' : 'asc';
    }
    onSortUpdated() {
        var _a;
        this.sorting =
            this.sorting.length === 0
                ? [
                    {
                        id: this.getResolvedSortField(),
                        desc: this.getResolvedSortDir() === 'desc',
                    },
                ]
                : this.sorting;
        (_a = this.table) === null || _a === void 0 ? void 0 : _a.setOptions(prev => (Object.assign(Object.assign({}, prev), { state: Object.assign(Object.assign({}, prev.state), { sorting: this.sorting }) })));
    }
    onColumnFiltersUpdated() {
        var _a;
        (_a = this.table) === null || _a === void 0 ? void 0 : _a.setOptions(prev => (Object.assign(Object.assign({}, prev), { state: Object.assign(Object.assign({}, prev.state), { columnFilters: this.columnFilters }) })));
    }
    onDataUpdated() {
        var _a;
        (_a = this.table) === null || _a === void 0 ? void 0 : _a.setOptions(prev => (Object.assign(Object.assign({}, prev), { data: this.data })));
    }
    connectedCallback() {
        super.connectedCallback();
        this.updateComplete.then(() => {
            var _a;
            const resolvedSortField = this.getResolvedSortField();
            const resolvedSortDir = this.getResolvedSortDir();
            const resolvedOptions = Object.assign(Object.assign({ state: Object.assign(Object.assign(Object.assign({}, this.tableState), (_a = this.options) === null || _a === void 0 ? void 0 : _a.state), { sorting: [
                        {
                            id: resolvedSortField,
                            desc: resolvedSortDir === 'desc',
                        },
                    ] }), onStateChange: () => { }, onSortingChange: this.onSortingChange, onColumnFiltersChange: this.onColumnFilter, renderFallbackValue: null }, this.options), { getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel(), getFilteredRowModel: getFilteredRowModel(), getFacetedRowModel: getFacetedRowModel(), getFacetedUniqueValues: getFacetedUniqueValues(), getFacetedMinMaxValues: getFacetedMinMaxValues(), globalFilterFn: fuzzyFilter, columns: this.columns, data: this.data, enableSortingRemoval: false });
            this.table = createTable(resolvedOptions);
            this.tableState = this.table.initialState;
            this.table.setOptions(prev => {
                var _a;
                return (Object.assign(Object.assign(Object.assign({}, prev), this.options), { state: Object.assign(Object.assign(Object.assign({}, this.tableState), (_a = this.options) === null || _a === void 0 ? void 0 : _a.state), { sorting: [
                            {
                                id: resolvedSortField,
                                desc: resolvedSortDir === 'desc',
                            },
                        ] }) }));
            });
        });
    }
    render() {
        var _a, _b, _c;
        return html `<table
            class=${classMap({ table: true, 'fixed-layout': this.fixedLayout })}
            part="base"
        >
            <thead class="thead">
                ${map((_a = this.table) === null || _a === void 0 ? void 0 : _a.getHeaderGroups(), headerGroup => html `<tr key=${headerGroup.id}>
                            ${headerGroup.headers.map(header => html `<th
                                        part="head-${header.id}"
                                        key=${header.id}
                                        colspan=${header.colSpan}
                                        @click=${(e) => {
            var _a;
            if (!e.defaultPrevented) {
                (_a = header.column.getToggleSortingHandler()) === null || _a === void 0 ? void 0 : _a(e);
            }
        }}
                                        class=${classMap({
            th: true,
            'cursor-pointer': header.column.getCanSort(),
            'select-none': !header.column.getCanSort(),
        })}
                                    >
                                        ${header.isPlaceholder
            ? null
            : flexRender(header.column.columnDef
                .header, header.getContext())}
                                        ${this.sortField === header.id
            ? this.sortDir === 'asc'
                ? ' 🔽'
                : ' 🔼'
            : ''}
                                        ${when(header.column.getCanFilter(), () => this.getFilterInput(this.table, header.column))}
                                    </th>`)}
                        </tr>`)}
            </thead>
            <tbody>
                ${map((_b = this.table) === null || _b === void 0 ? void 0 : _b.getRowModel().rows, row => html `<tr key=${row.id} class="tr">
                            ${row
            .getVisibleCells()
            .map(cell => html `<td key=${cell.id} class="td">
                                            ${flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>`)}
                        </tr>`)}
            </tbody>
            <tfoot>
                ${map((_c = this.table) === null || _c === void 0 ? void 0 : _c.getFooterGroups(), footerGroup => html `<tr key=${footerGroup.id}>
                            ${footerGroup.headers.map(header => html `<th key=${header.id}>
                                        ${header.isPlaceholder
            ? null
            : flexRender(header.column.columnDef
                .footer, header.getContext())}
                                    </th>`)}
                        </tr>`)}
            </tfoot>
        </table>`;
    }
}
SgdfTableNext.styles = styles;
export default SgdfTableNext;
__decorate([
    property({ type: Object })
], SgdfTableNext.prototype, "options", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SgdfTableNext.prototype, "fixedLayout", void 0);
__decorate([
    property({ type: Array })
], SgdfTableNext.prototype, "columns", void 0);
__decorate([
    property({ type: Array })
], SgdfTableNext.prototype, "data", void 0);
__decorate([
    state()
], SgdfTableNext.prototype, "table", void 0);
__decorate([
    state()
], SgdfTableNext.prototype, "tableState", void 0);
__decorate([
    property()
], SgdfTableNext.prototype, "sorting", void 0);
__decorate([
    property()
], SgdfTableNext.prototype, "sortField", void 0);
__decorate([
    property()
], SgdfTableNext.prototype, "sortDir", void 0);
__decorate([
    property()
], SgdfTableNext.prototype, "columnFilters", void 0);
__decorate([
    watch('sorting')
], SgdfTableNext.prototype, "onSortUpdated", null);
__decorate([
    watch('columnFilters')
], SgdfTableNext.prototype, "onColumnFiltersUpdated", null);
__decorate([
    watch('data')
], SgdfTableNext.prototype, "onDataUpdated", null);
//# sourceMappingURL=table-next.component.js.map